import { ImagePlus, Trash2 } from "lucide-react";
import { useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { API, errMsg, imgUrl } from "@/lib/api";

async function uploadFile(file) {
  const fd = new FormData();
  fd.append("file", file);
  const r = await API.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return r.data.url;
}

function useDragUpload(onFiles) {
  const [dragOver, setDragOver] = useState(false);
  const handlers = {
    onDragOver: (e) => {
      e.preventDefault();
      setDragOver(true);
    },
    onDragLeave: () => setDragOver(false),
    onDrop: (e) => {
      e.preventDefault();
      setDragOver(false);
      const files = [...(e.dataTransfer?.files || [])].filter((f) => f.type.startsWith("image/"));
      if (files.length) onFiles(files);
    },
  };
  return { dragOver, handlers };
}

export function ImageUpload({ value, onChange, label = "Image", testId = "image-upload" }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const uploadFiles = async (files) => {
    if (!files.length) return;
    setBusy(true);
    try {
      onChange(await uploadFile(files[0]));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const { dragOver, handlers } = useDragUpload(uploadFiles);

  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (file) uploadFiles([file]);
  };

  return (
    <div
      data-testid={testId}
      {...handlers}
      className={`rounded-lg transition-shadow ${dragOver ? "ring-2 ring-primary ring-offset-2 ring-offset-background" : ""}`}
    >
      {value ? (
        <div className="relative inline-block">
          <img src={imgUrl(value)} alt={label} className="h-28 w-40 object-contain rounded-lg border bg-muted" data-testid={`${testId}-preview`} />
          <button
            type="button"
            onClick={() => onChange("")}
            className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground rounded-full p-1"
            data-testid={`${testId}-remove-btn`}
            aria-label="Remove image"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      ) : (
        <div className={`flex items-center gap-2 rounded-lg border-2 border-dashed p-3 transition-colors ${dragOver ? "border-primary bg-primary/5" : "border-border"}`} data-testid={`${testId}-dropzone`}>
          <Button type="button" variant="outline" size="sm" disabled={busy} onClick={() => inputRef.current?.click()} data-testid={`${testId}-upload-btn`}>
            <ImagePlus className="mr-2 h-4 w-4" /> {busy ? "Uploading…" : `Upload ${label}`}
          </Button>
          <span className="text-xs text-muted-foreground">or drag &amp; drop an image here</span>
        </div>
      )}
      <Input
        className="mt-2"
        placeholder="…or paste an image URL"
        value={value?.startsWith("http") ? value : ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={`${testId}-url-input`}
      />
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={handle} data-testid={`${testId}-file-input`} />
    </div>
  );
}

export function MultiImageUpload({ images, onChange, testId = "multi-image-upload" }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [url, setUrl] = useState("");

  const uploadFiles = async (files) => {
    if (!files.length) return;
    setBusy(true);
    try {
      const uploaded = [];
      for (const f of files) {
        uploaded.push(await uploadFile(f));
      }
      onChange([...images, ...uploaded]);
      toast.success(files.length > 1 ? `${files.length} images uploaded` : "Image uploaded");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const { dragOver, handlers } = useDragUpload(uploadFiles);

  const handle = async (e) => {
    const files = [...(e.target.files || [])];
    if (files.length) uploadFiles(files);
  };

  return (
    <div
      data-testid={testId}
      {...handlers}
      className={`rounded-lg transition-shadow ${dragOver ? "ring-2 ring-primary ring-offset-2 ring-offset-background" : ""}`}
    >
      <div className={`flex flex-wrap gap-3 rounded-lg border-2 border-dashed p-3 transition-colors ${dragOver ? "border-primary bg-primary/5" : "border-border"}`} data-testid={`${testId}-dropzone`}>
        {images.map((im, i) => (
          <div key={i} className="relative">
            <img src={imgUrl(im)} alt="" className="h-24 w-24 object-contain rounded-lg border bg-muted" />
            <button
              type="button"
              onClick={() => onChange(images.filter((_, x) => x !== i))}
              className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground rounded-full p-1"
              data-testid={`${testId}-remove-${i}`}
              aria-label="Remove image"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          className="h-24 w-24 rounded-lg border-2 border-dashed flex flex-col items-center justify-center text-muted-foreground hover:border-primary hover:text-primary transition-colors"
          data-testid={`${testId}-add-btn`}
        >
          <ImagePlus className="h-5 w-5" />
          <span className="text-[10px] mt-1">{busy ? "Uploading…" : "Add / drop images"}</span>
        </button>
      </div>
      <div className="flex gap-2 mt-3">
        <Input placeholder="…or paste an image URL" value={url} onChange={(e) => setUrl(e.target.value)} data-testid={`${testId}-url-input`} />
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={!url.trim()}
          onClick={() => {
            onChange([...images, url.trim()]);
            setUrl("");
          }}
          data-testid={`${testId}-add-url-btn`}
        >
          Add
        </Button>
      </div>
      <input ref={inputRef} type="file" accept="image/*" multiple className="hidden" onChange={handle} data-testid={`${testId}-file-input`} />
    </div>
  );
}
